import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DownloadIcon,
  RefreshCw,
  MousePointerIcon,
  UsersIcon,
  BarChartIcon,
} from 'lucide-react';
import { tenantApi, CTAMetrics, CTAPerformance } from '../lib/tenant-api';
import DateRangePicker from '../components/DateRangePicker';

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  trend?: string;
  trendType?: 'positive' | 'negative';
}> = ({ icon, label, value, trend, trendType }) => {
  return (
    <div className="bg-white border border-gray-200  p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center text-gray-500 text-sm mb-2">
            <span className="mr-2">{icon}</span>
            <span>{label}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{value}</div>
          {trend && (
            <div className={`text-sm mt-2 font-medium ${trendType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
              {trend}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CTAPerformanceTable: React.FC<{
  data: CTAPerformance[];
}> = ({ data }) => {
  const [sortField, setSortField] = useState<keyof CTAPerformance>('total_clicks');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    return 0;
  });

  const handleSort = (field: keyof CTAPerformance) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="bg-white border border-gray-200  overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Individual CTA Performance</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => handleSort('cta_name')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                CTA Name
              </th>
              <th
                onClick={() => handleSort('total_clicks')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Total Clicks
              </th>
              <th
                onClick={() => handleSort('unique_sessions')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Unique users
              </th>
              <th
                onClick={() => handleSort('clicks_per_session')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Avg. Clicks/User
              </th>
              <th
                onClick={() => handleSort('clicks_per_conversation')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Clicks/Conversation
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length > 0 ? (
              sortedData.map((cta) => (
                <tr key={cta.cta_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {cta.cta_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cta.total_clicks}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cta.unique_sessions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cta.clicks_per_session.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cta.clicks_per_conversation.toFixed(1)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                  No CTA click data available for the selected period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function CTAClicks() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [trafficType, setTrafficType] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<string>(localStorage.getItem('cta_date_range_selected') || 'today');

  const { data: metrics, isLoading, refetch } = useQuery<CTAMetrics>({
    queryKey: ['cta-metrics', { date_from: dateFrom, date_to: dateTo, traffic_type: trafficType }],
    queryFn: () => tenantApi.getCTAMetrics({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      traffic_type: trafficType || undefined,
    }),
  });

  useEffect(() => {
    const persistedDateRange = localStorage.getItem('cta_date_range_type_selected');
    const persistedStartDate = localStorage.getItem('cta_date_range_start_date');
    const persistedEndDate = localStorage.getItem('cta_date_range_end_date');
    if (persistedDateRange && persistedStartDate && persistedEndDate) {
      const startDT = new Date(persistedStartDate);
      const endDT = new Date(persistedEndDate);
      handleDateRangeChange(persistedDateRange, startDT, endDT);
    }
  }, []);

  const handleDateRangeChange = (range: string, start?: Date, end?: Date) => {
    setSelectedDateRange(range);
    localStorage.setItem('cta_date_range_selected', range)
    if (start && end) {
      setDateFrom(`${start?.toISOString().split('T')[0]}`);
      setDateTo(`${end?.toISOString().split('T')[0]}`);
      refetch();
    }
  };

  const handleExport = () => {
    if (!metrics) return;

    const csvRows = [];

    // Add CTA clicks section
    if (metrics.by_cta && metrics.by_cta.length > 0) {
      csvRows.push(['CTA Name', 'Total Clicks', 'Unique Sessions', 'Clicks per Session', 'Clicks per Conversation'].join(','));

      metrics.by_cta.forEach((cta) => {
        csvRows.push([
          `"${cta.cta_name}"`,
          cta.total_clicks,
          cta.unique_sessions,
          cta.clicks_per_session.toFixed(2),
          cta.clicks_per_conversation.toFixed(2)
        ].join(','));
      });
    }

    // Add custom form clicks section if present
    if (metrics.custom_form_clicks && metrics.custom_form_clicks.length > 0) {
      if (csvRows.length > 0) {
        csvRows.push(''); // Empty line separator
      }
      csvRows.push(['Form Name', 'Total Clicks', 'Unique Sessions', 'Unique Conversations'].join(','));

      metrics.custom_form_clicks.forEach((form) => {
        csvRows.push([
          `"${form.display_name}"`,
          form.total_clicks,
          form.unique_sessions,
          form.unique_conversations
        ].join(','));
      });
    }

    // Check if we have any data to export
    if (csvRows.length === 0) {
      alert('No data available to export');
      return;
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cta-clicks-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading CTA metrics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/dashboard/engagement')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Engagement
          </button>
          <h1 className="text-3xl font-bold text-gray-900">CTA Clicks</h1>
          <p className="mt-1 text-sm text-gray-600">Button or link clicks</p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-gray-300  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <DownloadIcon className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200  p-4">
        <div className="flex items-center space-x-4">
          <DateRangePicker onDateRangeChange={handleDateRangeChange} selectedRange={selectedDateRange} />
          <div>
            {/* <label className="block text-xs font-medium text-gray-700 mb-1">Click Source</label> */}
            <select
              value={trafficType}
              onChange={(e) => setTrafficType(e.target.value)}
              className="block w-full  border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Click Source</option>
              <option value="website_organic">Website widget</option>
              <option value="landing_page">Lander Widget</option>
              <option value="website_sandbox">Sandbox</option>
            </select>
          </div>
          <div className="">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-gray-300  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          icon={<MousePointerIcon className="h-5 w-5" />}
          label="Total Clicks"
          value={metrics?.summary.total_clicks || 0}
          trend="+1 vs last period"
          trendType="positive"
        />
        <MetricCard
          icon={<UsersIcon className="h-5 w-5" />}
          label="Total Users"
          value={metrics?.summary.unique_sessions || 0}
          trend="+8 vs last period"
          trendType="positive"
        />
        <MetricCard
          icon={<BarChartIcon className="h-5 w-5" />}
          label="Avg. Clicks/User"
          value={
            metrics?.summary.unique_sessions
              ? (metrics.summary.total_clicks / metrics.summary.unique_sessions).toFixed(1)
              : '0.0'
          }
          trend="+1.2 vs last period"
          trendType="positive"
        />
      </div>

      {/* CTA Performance Table */}
      <CTAPerformanceTable data={metrics?.by_cta || []} />

      {/* Custom Forms Section */}
      {metrics?.custom_form_clicks && metrics.custom_form_clicks.length > 0 && (
        <div className="bg-white border border-gray-200  overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Custom Form Clicks</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Form Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unique Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unique Conversations
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.custom_form_clicks.map((form, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {form.display_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {form.total_clicks}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {form.unique_sessions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {form.unique_conversations}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
